import React, { useState } from 'react';
import { toast } from 'react-hot-toast';
import { DiTrello } from 'react-icons/di';

import { getTrelloAuthUrl, trelloIncrementalSync } from '../../shared/api/integration';

const TrelloIntegration: React.FC<{
  isConnected?: boolean;
  onDisconnect: (integrationType: string) => void;
  onUpdate?: () => void;
}> = ({ isConnected, onDisconnect, onUpdate }) => {
  const [isConnecting, setIsConnecting] = useState<boolean>(false);
  const [isSyncing, setIsSyncing] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleTrelloIncrementalSync = async () => {
    setErrorMessage(null);

    if (!isConnected) {
      toast.error('Trello is not connected. Please connect first.');
      return;
    }

    setIsSyncing(true);
    try {
      await trelloIncrementalSync();
      toast.success('Sync started.');
    } catch (e) {
      console.error('Trello incremental sync failed:', e);
      setErrorMessage('Failed to start sync.');
      toast.error('Failed to start sync.');
    } finally {
      setIsSyncing(false);
    }
  };

  const handleTrelloLogin = async () => {
    setErrorMessage(null);
    setIsConnecting(true);

    try {
      if (isConnected) {
        onDisconnect('trello');
        return;
      }

      const { url } = await getTrelloAuthUrl();
      if (url) {
        const popupWidth = 600;
        const popupHeight = 600;
        const left = window.innerWidth / 2 - popupWidth / 2;
        const top = window.innerHeight / 2 - popupHeight / 2;

        const popup = window.open(
          url,
          'trello-auth-popup',
          `width=${popupWidth},height=${popupHeight},top=${top},left=${left}`,
        );

        if (popup) {
          const handleMessage = (e: MessageEvent) => {
            if (e.data?.type === 'trello-connected') {
              window.removeEventListener('message', handleMessage);
              clearInterval(closeInterval);
              toast.success('Trello connected successfully.');
              if (onUpdate) onUpdate();
            }
          };
          window.addEventListener('message', handleMessage);

          // Fallback: if popup closes without postMessage (user skipped), still refresh
          const closeInterval = setInterval(() => {
            if (popup.closed) {
              clearInterval(closeInterval);
              window.removeEventListener('message', handleMessage);
              if (onUpdate) onUpdate();
            }
          }, 1000);
        }
      } else {
        setErrorMessage('Failed to open Trello authentication window.');
      }
    } catch (error) {
      console.error('Error during Trello authentication:', error);
      setErrorMessage('An error occurred while connecting to Trello.');
    } finally {
      setIsConnecting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <DiTrello className="text-2xl opacity-70" />
            <div className="text-lg font-semibold">Trello</div>
            <span className={`badge ${isConnected ? 'badge-success' : 'badge-ghost'}`}>
              {isConnected ? 'Connected' : 'Not connected'}
            </span>
          </div>
          <div className="text-sm opacity-70">Connect Trello to sync boards, lists and cards.</div>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            onClick={handleTrelloLogin}
            disabled={isConnecting || isSyncing}
            className={`btn btn-sm ${isConnected ? 'btn-outline' : 'btn-primary'}`}
          >
            {isConnecting ? (
              <>
                <span className="loading loading-spinner loading-xs" />
                Connecting…
              </>
            ) : isConnected ? (
              'Disconnect'
            ) : (
              'Connect'
            )}
          </button>

          <button
            onClick={handleTrelloIncrementalSync}
            disabled={isConnecting || isSyncing || !isConnected}
            className="btn btn-sm btn-outline"
            title={!isConnected ? 'Connect Trello to enable sync' : 'Start incremental sync'}
          >
            {isSyncing ? (
              <>
                <span className="loading loading-spinner loading-xs" />
                Syncing…
              </>
            ) : (
              'Sync now'
            )}
          </button>

          {isConnected && (
            <button className="btn btn-sm btn-ghost" onClick={onUpdate} disabled={isConnecting || isSyncing}>
              Refresh
            </button>
          )}
        </div>
      </div>

      {errorMessage && (
        <div className="alert alert-error">
          <span>{errorMessage}</span>
        </div>
      )}

      {!isConnected ? (
        <div className="alert bg-base-100 shadow-card">
          <span className="text-sm opacity-80">
            Click <b>Connect</b> to authorize Trello access. Your boards sync automatically right after connecting.
          </span>
        </div>
      ) : (
        <div className="alert alert-info">
          <span className="text-sm">
            Tip: run <b>Sync now</b> anytime you want fresh stats.
          </span>
        </div>
      )}

      <div className="collapse collapse-arrow bg-base-200">
        <input type="checkbox" />
        <div className="collapse-title font-medium">Permissions & OAuth details</div>
        <div className="collapse-content text-sm opacity-80 space-y-2">
          <div className="font-medium">MFlow may request access to:</div>
          <ul className="list-disc list-inside space-y-1">
            <li>Read boards, lists, and cards</li>
            <li>Write cards, comments, and more</li>
            <li>Manage boards, lists, and cards</li>
          </ul>

          <a
            className="link link-primary"
            rel="noreferrer"
            href="https://developer.atlassian.com/cloud/trello/guides/power-ups/your-first-power-up/"
            target="_blank"
          >
            How Trello OAuth works →
          </a>
        </div>
      </div>
    </div>
  );
};

export default TrelloIntegration;
